import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  PrimaryButton,
  SecondaryButton,
  ToggleSwitch,
  Badge,
} from "../../UI/DesignStandards";
import InlineAlert from "../../UI/DesignStandards/Notifications/InlineAlert";
import {
  createLeagueSheetSignedUrl,
  updateExtractedAverageValues,
  updateExtractedBowlerName,
} from "../../../utility/averageCatalogs";
import { createSignupAverageProofSignedUrl } from "../../../utility/signupAverageProof";
import { parseExtractedRowEdits, parseTournamentAverage } from "../../../utility/extractRowEdits";
import { displaySheetPage } from "../../../utility/sheetHighlight";
import { SheetRowPreview } from "./SheetRowPreview";

const draftFromRow = (row) => ({
  name: row?.name || "",
  average: row?.average == null ? "" : String(row.average),
  games: row?.games == null ? "" : String(row.games),
  pins: row?.pins == null ? "" : String(row.pins),
  hasAverage: row?.hasAverage !== false,
});

const draftsMatch = (draft, row) => {
  const original = draftFromRow(row);
  return (
    draft.name.trim() === original.name.trim() &&
    draft.average === original.average &&
    draft.games === original.games &&
    draft.pins === original.pins &&
    Boolean(draft.hasAverage) === Boolean(original.hasAverage)
  );
};

const formatCount = (value) => Number(value || 0).toLocaleString("en-US");

const fieldClass = (error, extra = "") =>
  `w-full px-3 py-2.5 border rounded-lg bg-white text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent ${
    error ? "border-red-300" : "border-brand-charcoal/20"
  } ${extra}`;

const ReviewField = ({ label, error, hint, children }) => (
  <label className="block min-w-0">
    <span className="block text-xs font-display font-bold uppercase tracking-wider text-brand-muted mb-1.5">
      {label}
    </span>
    {children}
    {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
    {hint ? <p className="mt-1 text-sm text-brand-muted">{hint}</p> : null}
  </label>
);

const selectedAverageForRow = (row) => {
  const list = row?.allAverages || [];
  if (list.length === 0) return null;
  const entered = Number(row?.average);
  const matchingEntered = list.find(
    (item) => Number(item.average) === entered
  );
  if (matchingEntered) return matchingEntered;
  const candidate = list.find(
    (item) => Number(item.average) === Number(row?.catalogAverage)
  );
  return candidate || list[0];
};

const initialSheetSelection = (row) => {
  const catalog = selectedAverageForRow(row);
  const catalogPath = catalog?.storagePath || row?.storagePath || "";
  if (catalogPath) return catalog?.id || null;
  return row?.signupProofs?.[0]?.id || null;
};

const averageIsInUse = (item, draft) =>
  draft?.hasAverage !== false &&
  item?.average != null &&
  draft?.average !== "" &&
  Number(item.average) === Number(draft.average);

const CatalogLeagueList = ({
  averages,
  draft,
  selectedAverageId,
  onView,
  onUse,
}) => {
  if (!averages?.length) return null;
  return (
    <div>
      <p className="text-xs font-display font-bold uppercase tracking-wider text-brand-muted mb-2">
        League averages
      </p>
      <p className="text-sm text-brand-charcoal mb-2">
        Click a league to view its sheet. The average in use is marked.
      </p>
      <div className="space-y-1" role="list" aria-label="League averages">
        {averages.map((item) => {
          const inUse = averageIsInUse(item, draft);
          const viewing = item.id === selectedAverageId;
          return (
            <div
              key={item.id}
              role="listitem"
              className={`rounded-lg border ${
                viewing
                  ? "border-brand-gold-ink ring-1 ring-brand-gold-ink bg-brand-gold/15"
                  : "border-brand-charcoal/20 bg-white hover:border-brand-charcoal"
              }`}
            >
              <button
                type="button"
                onClick={() => onView(item)}
                aria-current={viewing ? "true" : undefined}
                className="w-full text-left px-3 py-2 text-sm flex items-start gap-3 text-brand-charcoal"
              >
                <span
                  className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 ${
                    inUse
                      ? "border-brand-gold-ink bg-brand-gold"
                      : "border-brand-charcoal/30 bg-white"
                  }`}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold tabular-nums">
                      {item.average}
                    </span>
                    {inUse ? <Badge variant="primary">In use</Badge> : null}
                  </span>
                  <span className="block text-brand-muted">
                    {item.source || "League sheet"}
                    {item.games != null ? ` · ${item.games} games` : ""}
                    {item.qualifies === false ? " · Under minimum games" : ""}
                  </span>
                </span>
              </button>
              {viewing && !inUse ? (
                <div className="px-3 pb-2 pl-10">
                  <SecondaryButton
                    size="sm"
                    onClick={() => onUse(item)}
                    className="!px-3 !py-1.5 text-sm"
                  >
                    Use this average
                  </SecondaryButton>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ExtractReview = ({
  isOpen,
  onClose,
  rows = [],
  startIndex = 0,
  organizationID,
  onRowSaved,
  variant = "extract",
  onCommit,
  title = "Review extract",
}) => {
  const [index, setIndex] = useState(startIndex);
  const [draft, setDraft] = useState(draftFromRow(rows[startIndex]));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [paneWidth, setPaneWidth] = useState(560);
  const [selectedAverageId, setSelectedAverageId] = useState(
    selectedAverageForRow(rows[startIndex])?.id || null
  );
  const previewRef = useRef(null);
  const urlByPath = useRef(new Map());
  const openedRef = useRef(false);
  const goToRef = useRef(null);
  const indexRef = useRef(0);
  const lastRowRef = useRef(false);
  const savingRef = useRef(false);

  const row = rows[index] || null;
  const lastRow = index >= rows.length - 1;
  const selectedProof = (row?.signupProofs || []).find(
    (item) => item.id === selectedAverageId
  );
  const selectedAverage = selectedProof
    ? null
    : (row?.allAverages || []).find((item) => item.id === selectedAverageId) ||
      selectedAverageForRow(row);
  const previewStoragePath = selectedProof
    ? selectedProof.storagePath
    : selectedAverage?.storagePath || row?.storagePath || "";
  const previewIsProof = Boolean(selectedProof);
  const previewMimeType = selectedProof
    ? null
    : selectedAverage?.mimeType || row?.mimeType;
  const previewFileName = selectedProof
    ? selectedProof.fileName
    : selectedAverage?.fileName || row?.fileName;
  const previewPage = previewIsProof
    ? null
    : displaySheetPage(selectedAverage?.sourcePage ?? row?.sourcePage);
  const previewBox = previewIsProof
    ? null
    : selectedAverage?.boundingBox || row?.boundingBox;
  const highlightName = previewIsProof
    ? ""
    : row?.extractedName || row?.name || "";
  const isVerify = variant === "verify";

  useEffect(() => {
    if (!isOpen) {
      openedRef.current = false;
      return;
    }
    if (openedRef.current) return;
    openedRef.current = true;
    const nextIndex = Math.min(
      Math.max(startIndex, 0),
      Math.max(rows.length - 1, 0)
    );
    setIndex(nextIndex);
    setDraft(draftFromRow(rows[nextIndex]));
    setSelectedAverageId(initialSheetSelection(rows[nextIndex]));
    setErrors({});
    setSaveError("");
  }, [isOpen, startIndex, rows]);

  const rowId = row?.id;

  useEffect(() => {
    if (!rowId) {
      setDraft(draftFromRow(null));
      return;
    }
    setDraft(draftFromRow(row));
    setSelectedAverageId(initialSheetSelection(row));
    setErrors({});
    setSaveError("");
    // Reset the form when the director moves to another extracted row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowId]);

  useEffect(() => {
    let cancelled = false;
    const storagePath = previewStoragePath;
    const cacheKey = `${previewIsProof ? "proof" : "catalog"}:${storagePath}`;
    if (!isOpen || !storagePath) {
      setUrl("");
      setUrlError(
        row && !previewIsProof ? "This league sheet file is not stored." : ""
      );
      return undefined;
    }
    const cached = urlByPath.current.get(cacheKey);
    if (cached) {
      setUrl(cached);
      setUrlError("");
      return undefined;
    }
    setUrl("");
    setUrlError("");
    const sign = previewIsProof
      ? createSignupAverageProofSignedUrl
      : createLeagueSheetSignedUrl;
    sign(storagePath, 3600)
      .then((signedUrl) => {
        urlByPath.current.set(cacheKey, signedUrl);
        if (!cancelled) setUrl(signedUrl);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setUrlError(
            err.message ||
              (previewIsProof
                ? "Could not open average verification."
                : "Could not open this league sheet.")
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, previewStoragePath, previewIsProof, row?.id]);

  useEffect(() => {
    if (!previewRef.current) return undefined;
    const pane = previewRef.current;
    const updateWidth = () => setPaneWidth(pane.clientWidth || 560);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(pane);
    return () => observer.disconnect();
  }, [url, isOpen, index]);

  const dirty = useMemo(
    () => (row ? !draftsMatch(draft, row) : false),
    [draft, row]
  );

  const saveCurrent = async () => {
    if (!row) return true;
    if (variant === "verify") {
      const parsed = parseTournamentAverage(draft.average, {
        hasAverage: draft.hasAverage !== false,
      });
      if (parsed.error) {
        setErrors({ average: parsed.error });
        return false;
      }
      setSaving(true);
      setSaveError("");
      try {
        await onCommit?.(row, {
          average: parsed.value,
          hasAverage: parsed.hasAverage !== false,
        });
        setErrors({});
        return true;
      } catch (err) {
        console.error(err);
        setSaveError(err.message || "Could not verify this average.");
        return false;
      } finally {
        setSaving(false);
      }
    }
    if (!organizationID) return true;
    if (!dirty) return true;
    const parsed = parseExtractedRowEdits(draft);
    if (Object.keys(parsed.errors).length) {
      setErrors(parsed.errors);
      return false;
    }
    setSaving(true);
    setSaveError("");
    try {
      if (parsed.values.extractedName !== (row.name || "").trim()) {
        await updateExtractedBowlerName({
          extractedBowlerID: row.extractedBowlerID,
          organizationID,
          name: parsed.values.extractedName,
        });
      }
      if (
        parsed.values.average !== row.average ||
        parsed.values.games !== (row.games ?? null) ||
        parsed.values.pins !== (row.pins ?? null)
      ) {
        await updateExtractedAverageValues({
          extractedAverageID: row.extractedAverageID,
          organizationID,
          average: parsed.values.average,
          games: parsed.values.games,
          pins: parsed.values.pins,
        });
      }
      onRowSaved?.({
        extractedBowlerID: row.extractedBowlerID,
        extractedAverageID: row.extractedAverageID,
        name: parsed.values.extractedName,
        average: parsed.values.average,
        games: parsed.values.games,
        pins: parsed.values.pins,
      });
      setErrors({});
      return true;
    } catch (err) {
      console.error(err);
      setSaveError(err.message || "Could not save these values.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goTo = async (nextIndex) => {
    if (variant === "verify") {
      if (nextIndex > index) {
        const saved = await saveCurrent();
        if (!saved) return;
      }
      setIndex(nextIndex);
      return;
    }
    const saved = await saveCurrent();
    if (!saved) return;
    setIndex(nextIndex);
  };

  const handleClose = async () => {
    if (variant === "verify") {
      onClose();
      return;
    }
    const saved = await saveCurrent();
    if (!saved) return;
    onClose();
  };

  const handlePrimary = async () => {
    if (variant === "verify") {
      const saved = await saveCurrent();
      if (!saved) return;
      if (lastRow) {
        onClose();
        return;
      }
      setIndex(index + 1);
      return;
    }
    if (lastRow) {
      handleClose();
      return;
    }
    goTo(index + 1);
  };

  const setField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  goToRef.current = goTo;
  indexRef.current = index;
  lastRowRef.current = lastRow;
  savingRef.current = saving;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (savingRef.current) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (event.key === "ArrowRight" && !lastRowRef.current) {
        event.preventDefault();
        goToRef.current(indexRef.current + 1);
      }
      if (event.key === "ArrowLeft" && indexRef.current > 0) {
        event.preventDefault();
        goToRef.current(indexRef.current - 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const progressPct = rows.length
    ? Math.round(((index + 1) / rows.length) * 100)
    : 0;
  const primaryLabel = isVerify
    ? lastRow
      ? "Verify and finish"
      : "Verify and next"
    : lastRow
    ? dirty
      ? "Save and finish"
      : "Finish"
    : dirty
    ? "Save and next"
    : "Next";
  const sheetLabel = previewIsProof
    ? "Average verification"
    : selectedAverage?.source || row?.source || row?.fileName || "League sheet";
  const signupProofs = row?.signupProofs || [];
  const showSignupProofs =
    signupProofs.length > 1 ||
    (signupProofs.length > 0 &&
      Boolean(selectedAverage?.storagePath || row?.storagePath));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      subtitle={
        rows.length
          ? `${formatCount(index + 1)} of ${formatCount(rows.length)}`
          : isVerify
          ? "No averages left to review."
          : "No extracted averages to review."
      }
      maxWidth="max-w-7xl"
      className="h-[95vh] !max-h-[95vh] sm:h-[95vh] sm:!max-h-[95vh]"
      bodyClassName="!overflow-hidden flex flex-col !pt-0"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SecondaryButton
            type="button"
            onClick={() => goTo(index - 1)}
            disabled={!row || index === 0 || saving}
          >
            Previous
          </SecondaryButton>
          <div className="flex flex-wrap items-center gap-3">
            {dirty && !isVerify ? (
              <span className="text-sm text-brand-muted">Unsaved changes</span>
            ) : null}
            <SecondaryButton
              type="button"
              onClick={handleClose}
              disabled={saving}
            >
              Done
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={handlePrimary}
              disabled={!row || saving}
              loading={saving}
            >
              {primaryLabel}
            </PrimaryButton>
          </div>
        </div>
      }
    >
      {row ? (
        <div className="flex-1 min-h-0 flex flex-col gap-4">
          <div
            className="h-1 bg-brand-cream shrink-0 -mx-4 sm:-mx-6"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={rows.length}
            aria-valuenow={index + 1}
            aria-label="Review progress"
          >
            <div
              className="h-full bg-brand-gold transition-[width] duration-200"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          {saveError ? (
            <InlineAlert
              type="error"
              title="Could not save"
              message={saveError}
              className="shrink-0"
            />
          ) : null}
          <div className="flex-1 min-h-0 flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(19rem,22rem)] lg:grid-rows-1">
            <div className="min-h-[12rem] flex-1 lg:min-h-0 flex flex-col h-full">
              {!previewStoragePath ? (
                <div className="flex-1 min-h-0 flex items-center justify-center border border-brand-charcoal/10 rounded-lg bg-brand-cream p-6">
                  <p className="text-sm text-brand-muted text-center">
                    No catalog sheet for this bowler.
                  </p>
                </div>
              ) : urlError ? (
                <p className="text-sm text-brand-charcoal">{urlError}</p>
              ) : !url ? (
                <p className="text-sm text-brand-muted">Opening league sheet.</p>
              ) : (
                <div
                  ref={previewRef}
                  className="flex-1 min-h-0 h-0 overflow-hidden border border-brand-charcoal/10 rounded-lg bg-brand-cream p-3"
                >
                  <SheetRowPreview
                    url={url}
                    storagePath={previewStoragePath}
                    mimeType={previewMimeType}
                    fileName={previewFileName}
                    sourcePage={selectedAverage?.sourcePage ?? row.sourcePage}
                    boundingBox={previewBox}
                    highlightName={highlightName}
                    highlightAverage={
                      previewIsProof
                        ? null
                        : selectedAverage?.average ??
                          row.catalogAverage ??
                          row.average
                    }
                    highlightGames={
                      previewIsProof ? null : selectedAverage?.games ?? row.games
                    }
                    highlightPins={
                      previewIsProof ? null : selectedAverage?.pins ?? row.pins
                    }
                    containerWidth={paneWidth}
                    toolbarLeft={
                      <div className="min-w-0">
                        <p
                          className="text-sm font-medium text-brand-charcoal truncate"
                          title={previewFileName || sheetLabel}
                        >
                          {sheetLabel}
                        </p>
                        <p className="text-xs text-brand-muted">
                          {previewIsProof
                            ? previewFileName || "Photo or PDF"
                            : previewPage
                            ? `Page ${previewPage}`
                            : "Sheet"}
                          {!previewIsProof && !previewBox
                            ? " · No saved location on this sheet"
                            : ""}
                        </p>
                      </div>
                    }
                  />
                </div>
              )}
            </div>
            <aside className="card-board p-4 sm:p-5 shrink-0 lg:min-h-0 lg:h-full lg:overflow-y-auto">
              <div className="space-y-4">
                <ReviewField label="Name" error={isVerify ? undefined : errors.name}>
                  {isVerify ? (
                    <p className="text-base font-medium text-brand-charcoal">
                      {row.name}
                    </p>
                  ) : (
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(event) => setField("name", event.target.value)}
                      className={fieldClass(errors.name)}
                      autoComplete="off"
                    />
                  )}
                </ReviewField>
                {isVerify &&
                row.extractedName &&
                row.extractedName !== row.name ? (
                  <p className="text-sm text-brand-muted">
                    Sheet has {row.extractedName}.
                  </p>
                ) : null}
                {isVerify ? (
                  <div className="flex items-center space-x-2">
                    <ToggleSwitch
                      checked={draft.hasAverage !== false}
                      onChange={(checked) => {
                        setDraft((current) => ({
                          ...current,
                          hasAverage: checked,
                          average: checked
                            ? current.average ||
                              (row.catalogAverage != null
                                ? String(row.catalogAverage)
                                : "")
                            : "",
                        }));
                        setErrors((current) => ({
                          ...current,
                          average: undefined,
                        }));
                      }}
                      size="sm"
                    />
                    <span className="text-sm text-brand-charcoal">
                      {draft.hasAverage !== false
                        ? "Has Average"
                        : "No Average"}
                    </span>
                  </div>
                ) : null}
                {!isVerify || draft.hasAverage !== false ? (
                  <ReviewField label="Average" error={errors.average}>
                    <input
                      type="number"
                      min="0"
                      max="300"
                      step="1"
                      inputMode="numeric"
                      value={draft.average}
                      onChange={(event) =>
                        setField("average", event.target.value)
                      }
                      className={fieldClass(
                        errors.average,
                        "text-2xl font-display font-bold tabular-nums"
                      )}
                    />
                  </ReviewField>
                ) : null}
                {isVerify ? (
                  <div className="space-y-3">
                    {row.catalogKind === "noQualifyingAverage" ? (
                      <p className="text-sm text-brand-charcoal">
                        {row.reason ||
                          "No extracted average meets the minimum games."}
                      </p>
                    ) : null}
                    {row.catalogKind === "disagrees" &&
                    (row.allAverages || []).length < 2 ? (
                      <p className="text-sm text-brand-charcoal">
                        Catalog has {row.catalogAverage}
                        {row.games != null ? ` (${row.games} games)` : ""}.
                      </p>
                    ) : null}
                    {(row.allAverages || []).length > 1 ||
                    (row.catalogKind === "noQualifyingAverage" &&
                      (row.allAverages || []).length > 0) ? (
                      <CatalogLeagueList
                        averages={row.allAverages}
                        draft={draft}
                        selectedAverageId={
                          selectedAverage?.id || selectedAverageId
                        }
                        onView={(item) => setSelectedAverageId(item.id)}
                        onUse={(item) => {
                          setSelectedAverageId(item.id);
                          setDraft((current) => ({
                            ...current,
                            hasAverage: true,
                            average:
                              item.average != null
                                ? String(item.average)
                                : current.average,
                          }));
                          setErrors((current) => ({
                            ...current,
                            average: undefined,
                          }));
                        }}
                      />
                    ) : null}
                    {showSignupProofs ? (
                      <div>
                        <p className="text-xs font-display font-bold uppercase tracking-wider text-brand-muted mb-2">
                          Average verification
                        </p>
                        <div
                          className="space-y-1"
                          role="list"
                          aria-label="Average verification"
                        >
                          {signupProofs.map((proof) => {
                            const viewing = proof.id === selectedAverageId;
                            return (
                              <button
                                key={proof.id}
                                type="button"
                                role="listitem"
                                onClick={() => setSelectedAverageId(proof.id)}
                                aria-current={viewing ? "true" : undefined}
                                className={`w-full text-left px-3 py-2 text-sm rounded-lg border ${
                                  viewing
                                    ? "border-brand-gold-ink ring-1 ring-brand-gold-ink bg-brand-gold/15"
                                    : "border-brand-charcoal/20 bg-white hover:border-brand-charcoal"
                                }`}
                              >
                                <span className="block font-medium text-brand-charcoal">
                                  Average verification
                                </span>
                                {signupProofs.length > 1 ? (
                                  <span className="block text-brand-muted">
                                    {proof.fileName}
                                  </span>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                    {(row.allAverages || []).length > 1 ||
                    (row.catalogKind === "noQualifyingAverage" &&
                      (row.allAverages || []).length > 0) ||
                    showSignupProofs ? null : row.games != null ||
                      row.pins != null ? (
                      <p className="text-sm text-brand-muted">
                        {row.games != null ? `${row.games} games` : ""}
                        {row.games != null && row.pins != null ? " · " : ""}
                        {row.pins != null ? `${row.pins} pins` : ""}
                        {row.source ? ` · ${row.source}` : ""}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <ReviewField
                      label="Games"
                      error={errors.games}
                      hint={
                        row.gamesCalculated
                          ? "Calculated from pins and average."
                          : null
                      }
                    >
                      <input
                        type="number"
                        min="0"
                        max="400"
                        step="1"
                        inputMode="numeric"
                        value={draft.games}
                        onChange={(event) =>
                          setField("games", event.target.value)
                        }
                        className={fieldClass(errors.games, "tabular-nums")}
                      />
                    </ReviewField>
                    <ReviewField label="Pins" error={errors.pins}>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        inputMode="numeric"
                        value={draft.pins}
                        onChange={(event) =>
                          setField("pins", event.target.value)
                        }
                        className={fieldClass(errors.pins, "tabular-nums")}
                      />
                    </ReviewField>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      ) : (
        <p className="text-sm text-brand-muted">
          {isVerify
            ? "No averages left to review."
            : "No extracted averages to review."}
        </p>
      )}
    </Modal>
  );
};

export default ExtractReview;
